import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal, ModalProps, DialogFooter } from './';
import { Button, Paragraph } from '@app/shared/ui/atoms/Common';
import { capitalize } from '@app/shared/utils/common';

interface BaseProps extends Omit<ModalProps, 'hiddenTitle' | 'children'> {
  actionType: 'confirm' | 'delete';
  onCancel: () => void;
  onConfirm: () => void;
}

interface PropsWithCustomTitle extends BaseProps {
  withCustomTitle: true;
  title: ReactNode;
  hiddenTitle: string;
}

interface PropsWithoutCustomTitle extends BaseProps {
  withCustomTitle: false;
  actionName: string;
  itemType: string;
  itemName: string;
}

type ConfirmationModalProps = PropsWithCustomTitle | PropsWithoutCustomTitle;

export function ConfirmationModal({
  isOpen,
  onOpenChange,
  featuredImg,
  featuredImgPosition,
  featuredImgBackgroundVariant,
  displayCloseButton = true,
  contentClassName,
  actionType,
  onCancel,
  onConfirm,
  ...props
}: ConfirmationModalProps) {
  const { t } = useTranslation();

  const getProps = () => {
    if (props.withCustomTitle) {
      return { hiddenTitle: props.hiddenTitle, title: props.title };
    }

    return {
      hiddenTitle: `${capitalize(props.actionName)} ${props.itemType}`,
      title: (
        <>
          {t('confirmation_modal.title_prefix')} {props.actionName}{' '}
          <span className="text-accent">{props.itemName}</span> {props.itemType}?
        </>
      ),
    };
  };

  const { hiddenTitle, title } = getProps();

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      featuredImg={featuredImg}
      featuredImgPosition={featuredImgPosition}
      featuredImgBackgroundVariant={featuredImgBackgroundVariant}
      displayCloseButton={displayCloseButton}
      contentClassName={contentClassName}
      hiddenTitle={hiddenTitle}
    >
      <Paragraph variant="white" className="my-4 px-10">
        {title}
      </Paragraph>

      <DialogFooter>
        <Button variant="secondary" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button variant={actionType === 'confirm' ? 'accent' : 'danger'} onClick={onConfirm}>
          {t('common.ok')}
        </Button>
      </DialogFooter>
    </Modal>
  );
}
